/**
 * Invitation Types page (/invitation-types) — list + Add/Edit form (view toggle),
 * following the Event Types screen pattern.
 *
 * Archive is only offered for inactive types (409 INVITATION_TYPE_ACTIVE otherwise);
 * archived types open read-only.
 */

import * as React from 'react'
import { useApiQuery, useApiMutation } from '@/api/ApiQueryServiceProvider'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/contexts/ToastContext'
import { AppLogger } from '@/lib/logger'
import { Search, Edit, Trash2, Plus, Loader2, MailPlus } from 'lucide-react'
import type { InvitationType } from '@/types/users'
import { InvitationTypeForm } from './InvitationTypeForm'

const log = AppLogger.getLogger(import.meta)

export function InvitationTypeList() {
  const { toast } = useToast()
  const [includeArchived, setIncludeArchived] = React.useState(false)
  const { data, isLoading, isError, refetch } = useApiQuery(
    'getInvitationTypes', includeArchived ? { includeArchived: 'true' } : {}, {},
  )
  const list: InvitationType[] = (data as any)?.content?.invitationTypes ?? []
  const archiveMut = useApiMutation('archiveInvitationType', 'DELETE', { invalidates: ['getInvitationTypes'] })

  const [view, setView] = React.useState<'list' | 'form'>('list')
  const [selected, setSelected] = React.useState<InvitationType | undefined>()
  const [archiving, setArchiving] = React.useState<InvitationType | null>(null)
  const [search, setSearch] = React.useState('')

  const open = (t?: InvitationType) => { setSelected(t); setView('form') }
  const close = () => { setSelected(undefined); setView('list') }

  const handleArchive = async () => {
    if (!archiving) return
    try {
      await archiveMut.mutateAsync({ invitationTypeNumber: archiving.invitationTypeNumber })
      toast({ title: 'Invitation type archived', type: 'success' })
    } catch (e) {
      log.error('archive failed', e)
      toast({ title: 'Could not archive', description: (e as Error)?.message, type: 'error' })
    }
    setArchiving(null)
  }

  if (view === 'form') return <InvitationTypeForm initial={selected} onSuccess={close} onCancel={close} />

  if (isLoading) return <Card className="shadow-sm"><CardContent className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></CardContent></Card>
  if (isError) return <Card className="shadow-sm"><CardContent className="flex flex-col items-center justify-center py-12 gap-4"><p className="text-destructive">Failed to load invitation types</p><Button variant="outline" onClick={() => refetch()}>Try Again</Button></CardContent></Card>

  const q = search.trim().toLowerCase()
  const filtered = list
    .filter((t) => !q || [t.invitationTypeLabel, t.invitationTypeName, t.invitationTypeDescription].some((f) => (f ?? '').toLowerCase().includes(q)))
    .sort((a, b) => a.invitationTypeLabel.localeCompare(b.invitationTypeLabel))

  return (
    <div className="space-y-4">
      <Card className="shadow-sm pb-0">
        <CardHeader className="border-b bg-slate-700 text-white [&_p]:text-slate-300">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10"><MailPlus className="h-5 w-5 text-primary" /></div>
              <div><CardTitle className="text-xl">Invitation Types</CardTitle><CardDescription>The reasons people can be invited</CardDescription></div>
            </div>
            <Button size="sm" className="gap-2" onClick={() => open()}><Plus className="h-4 w-4" />Add Invitation Type</Button>
          </div>
        </CardHeader>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Search invitation types..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
            <div className="flex items-center gap-2">
              <Switch id="include-archived" checked={includeArchived} onCheckedChange={setIncludeArchived} />
              <Label htmlFor="include-archived">Show archived</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          <Table className="text-base">
            <TableHeader>
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                <TableHead className="font-semibold">Label</TableHead>
                <TableHead className="font-semibold">Name</TableHead>
                <TableHead className="font-semibold">Target</TableHead>
                <TableHead className="font-semibold text-center">Status</TableHead>
                <TableHead className="font-semibold text-center">Pending</TableHead>
                <TableHead className="font-semibold text-center">Handler</TableHead>
                <TableHead className="w-[100px] text-right font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length > 0 ? filtered.map((t) => {
                const archived = !!t.archiveDate
                return (
                  <TableRow key={t.invitationTypeNumber ?? t.invitationTypeName} className="hover:bg-muted/50">
                    <TableCell className="font-medium">
                      <button className="text-primary hover:underline text-left" onClick={() => open(t)}>{t.invitationTypeLabel}</button>
                    </TableCell>
                    <TableCell className="font-mono text-sm text-muted-foreground">{t.invitationTypeName}</TableCell>
                    <TableCell className="capitalize">{t.targetKind}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant={!archived && t.status === 'active' ? 'default' : 'secondary'}>{archived ? 'Archived' : t.status === 'active' ? 'Active' : 'Inactive'}</Badge>
                    </TableCell>
                    <TableCell className="text-center"><Badge variant="secondary">{t.pendingInvitationCount ?? 0}</Badge></TableCell>
                    <TableCell className="text-center">
                      {t.hasHandler ? <Badge variant="outline">Deployed</Badge> : <span className="text-sm text-amber-600">Not deployed</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      {!archived && (
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => open(t)}><Edit className="h-4 w-4" /><span className="sr-only">Edit {t.invitationTypeLabel}</span></Button>
                          <Button
                            variant="ghost" size="icon"
                            className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                            disabled={t.status === 'active'}
                            title={t.status === 'active' ? 'Deactivate it before archiving' : 'Archive'}
                            onClick={() => setArchiving(t)}
                          >
                            <Trash2 className="h-4 w-4" /><span className="sr-only">Archive {t.invitationTypeLabel}</span>
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                )
              }) : (
                <TableRow><TableCell colSpan={7} className="h-24 text-center text-muted-foreground">{q ? 'No invitation types match your search.' : 'No invitation types yet.'}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!archiving} onOpenChange={(o) => !o && setArchiving(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Archive "{archiving?.invitationTypeLabel}"?</DialogTitle>
            <DialogDescription>
              It becomes read-only and its name can never be reused. Invitations already sent
              ({archiving?.pendingInvitationCount ?? 0} pending) aren't affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiving(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleArchive} disabled={archiveMut.isPending}>Archive</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
